import { useState, useCallback, useRef, useEffect } from "react";

/**
 * Prevents double-click / rapid-click on action buttons.
 * Usage:
 *   const [locked, runAction] = useActionLock();
 *   <button disabled={locked} onClick={() => runAction(async () => { ... })}>
 *
 * The button stays disabled from the moment it's clicked until the
 * async action resolves or rejects (or the component unmounts).
 */
const useActionLock = () => {
  const [locked, setLocked] = useState(false);
  const mountedRef = useRef(true);
  const lockRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const runAction = useCallback(async (action) => {
    if (lockRef.current) return;
    lockRef.current = true;
    setLocked(true);
    try {
      await action();
    } finally {
      lockRef.current = false;
      if (mountedRef.current) {
        setLocked(false);
      }
    }
  }, []);

  return [locked, runAction];
};

export default useActionLock;
